/**
 * The /cho-duyet inbox (decision AL1, plan `bank-notice-inbox` step N2).
 *
 * Rows are staged by a machine (Apps Script reading the bank's confirmation email) through
 * `app.stage_pending_entries`; this screen only reads, edits, approves and rejects them. Only
 * approval touches the ledger, and the entry is built in SQL (`app.approve_pending_entry`) so
 * the same rules apply whoever approves.
 */
import type { PlanningNature, TransferPurpose } from '@pfo/domain';
import type { Json } from '@pfo/database';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';
import type {
  NamedRef,
  PendingEntry,
  PendingInbox,
  PendingKind,
  PendingPatch,
} from '../model/pendingEntry.js';

type RawRef = { id: string; name: string } | null;

type RawItem = {
  id: string;
  occurred_at: string;
  amount: number | string;
  kind: PendingKind;
  counterparty: string | null;
  note: string | null;
  bank: string | null;
  account: { id: string; name: string };
  to_account: RawRef;
  suggested_category: RawRef;
  category: RawRef;
  planning_nature: PlanningNature | null;
  purpose: TransferPurpose | null;
  for_family: boolean;
  similar: Array<{
    id: string;
    amount: number | string;
    occurred_at: string;
    description: string | null;
    source: string;
  }> | null;
};

type RawInbox = { count: number; total: number | string; items: RawItem[] | null };

function refOf(raw: RawRef): NamedRef | null {
  return raw === null ? null : { id: raw.id, name: raw.name };
}

function toPendingEntry(raw: RawItem): PendingEntry {
  return {
    id: raw.id,
    occurredAt: raw.occurred_at,
    // numeric arrives as a JSON number; converted for display only, never added up here.
    amount: Number(raw.amount),
    kind: raw.kind,
    counterparty: raw.counterparty,
    note: raw.note,
    bank: raw.bank,
    account: { id: raw.account.id, name: raw.account.name },
    toAccount: refOf(raw.to_account),
    suggestedCategory: refOf(raw.suggested_category),
    category: refOf(raw.category),
    planningNature: raw.planning_nature,
    purpose: raw.purpose,
    forFamily: raw.for_family,
    similar: (raw.similar ?? []).map((line) => ({
      id: line.id,
      amount: Number(line.amount),
      occurredAt: line.occurred_at,
      description: line.description,
      source: line.source,
    })),
  };
}

export async function fetchPendingInbox(): Promise<PendingInbox> {
  const { data, error } = await supabase.schema('app').rpc('list_pending_entries');
  if (error) throw appErrorException(error);

  const raw = data as unknown as RawInbox;
  return {
    count: raw.count,
    total: Number(raw.total),
    items: (raw.items ?? []).map(toPendingEntry),
  };
}

/** camelCase patch → the snake_case keys the RPC reads. Absent keys stay absent. */
function toRpcPatch(patch: PendingPatch): Json {
  const out: Record<string, unknown> = {};
  if (patch.kind !== undefined) out['kind'] = patch.kind;
  if (patch.amount !== undefined) out['amount'] = patch.amount;
  if (patch.occurredAt !== undefined) out['occurred_at'] = patch.occurredAt;
  if (patch.note !== undefined) out['note'] = patch.note;
  if (patch.accountId !== undefined) out['account_id'] = patch.accountId;
  if (patch.toAccountId !== undefined) out['to_account_id'] = patch.toAccountId;
  if (patch.categoryId !== undefined) out['category_id'] = patch.categoryId;
  if (patch.planningNature !== undefined) out['planning_nature'] = patch.planningNature;
  if (patch.purpose !== undefined) out['purpose'] = patch.purpose;
  if (patch.forFamily !== undefined) out['for_family'] = patch.forFamily;
  return out as Json;
}

/** Posts the row as a real entry. Returns the journal entry id. */
export async function approvePendingEntry(id: string, patch: PendingPatch = {}): Promise<string> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('approve_pending_entry', { p_id: id, p_overrides: toRpcPatch(patch) });
  if (error) throw appErrorException(error);
  return data;
}

/** "Lưu, duyệt sau": keeps the edit, the row stays pending. */
export async function updatePendingEntry(id: string, patch: PendingPatch): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('update_pending_entry', { p_id: id, p_patch: toRpcPatch(patch) });
  if (error) throw appErrorException(error);
}

/** "Huỷ": the ledger never hears of it. */
export async function rejectPendingEntry(id: string, reason?: string): Promise<void> {
  const { error } = await supabase.schema('app').rpc('reject_pending_entry', {
    p_id: id,
    ...(reason === undefined ? {} : { p_reason: reason }),
  });
  if (error) throw appErrorException(error);
}
